import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import { X, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  flex-shrink: 0;
`;

const TitleSection = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  color: #111827;
  font-size: 1.25rem;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
  flex-shrink: 0;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const UserInfo = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const UserDetail = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid #e5e7eb;
  }
`;

const UserLabel = styled.span`
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
`;

const UserValue = styled.span`
  color: #111827;
  font-size: 0.875rem;
  font-weight: 600;
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  ${props => {
    switch(props.role) {
      case 'super_admin':
        return 'background: #fee2e2; color: #991b1b;';
      case 'admin':
        return 'background: #fef3c7; color: #92400e;';
      case 'lead':
        return 'background: #dbeafe; color: #1e40af;';
      case 'case_agent':
        return 'background: #d1fae5; color: #065f46;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const WarningBox = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const WarningContent = styled.div`
  flex: 1;
`;

const WarningTitle = styled.div`
  font-weight: 600;
  color: #92400e;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const WarningText = styled.div`
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`;

const DeleteButton = styled(Button)`
  background: #dc2626;
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    background: #b91c1c;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
  }
`;

function DeleteUserModal({ isOpen, onClose, user, currentUserId, onDelete, loading = false }) {
  const [apiError, setApiError] = useState('');

  const isSelfDelete = user?.id === currentUserId;

  const handleDelete = async () => {
    if (isSelfDelete) {
      setApiError('You cannot delete your own account');
      return;
    }

    try {
      await onDelete(user.id);
      onClose();
    } catch (error) {
      // Handle FastAPI validation errors (array of error objects)
      let errorMessage = 'Failed to delete user';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setApiError(errorMessage);
    }
  };

  if (!isOpen || !user) return null;

  const modalContent = (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <HeaderContent>
            <IconWrapper>
              <AlertTriangle size={24} />
            </IconWrapper>
            <TitleSection>
              <ModalTitle>Delete User</ModalTitle>
              <Subtitle>This action cannot be undone</Subtitle>
            </TitleSection>
          </HeaderContent>
          <CloseButton onClick={onClose} disabled={loading}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        {apiError && (
          <ErrorMessage>
            <AlertTriangle size={16} />
            {apiError}
          </ErrorMessage>
        )}

        {isSelfDelete && (
          <WarningBox>
            <AlertTriangle size={20} color="#f59e0b" />
            <WarningContent>
              <WarningTitle>Cannot Delete Own Account</WarningTitle>
              <WarningText>
                You cannot delete your own account. Please ask another administrator to perform this action.
              </WarningText>
            </WarningContent>
          </WarningBox>
        )}

        <UserInfo>
          <UserDetail>
            <UserLabel>Username</UserLabel>
            <UserValue>{user.username}</UserValue>
          </UserDetail>
          <UserDetail>
            <UserLabel>Full Name</UserLabel>
            <UserValue>{user.full_name || 'N/A'}</UserValue>
          </UserDetail>
          <UserDetail>
            <UserLabel>Email</UserLabel>
            <UserValue>{user.email || 'N/A'}</UserValue>
          </UserDetail>
          <UserDetail>
            <UserLabel>Role</UserLabel>
            <UserValue>
              <RoleBadge role={user.role}>
                {user.role.replace('_', ' ').toUpperCase()}
              </RoleBadge>
            </UserValue>
          </UserDetail>
        </UserInfo>

        {!isSelfDelete && (
          <WarningBox>
            <AlertTriangle size={20} color="#f59e0b" />
            <WarningContent>
              <WarningTitle>Are you sure?</WarningTitle>
              <WarningText>
                This will permanently delete <strong>{user.full_name || user.username}</strong> and remove all their access to the system. This action cannot be reversed.
              </WarningText>
            </WarningContent>
          </WarningBox>
        )}

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose} disabled={loading}>
            Cancel
          </CancelButton>
          <DeleteButton
            type="button"
            onClick={handleDelete}
            disabled={loading || isSelfDelete}
            data-cy="delete-user-confirm"
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={16} />
                Delete User
              </>
            )}
          </DeleteButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default DeleteUserModal;

